import { ref, computed, watch } from 'vue';
import type { Customer } from '@/types/customer';

export function useOrderCustomer(form: any, allowTakeawayWithoutCustomer?: import('vue').Ref<boolean>) {
  // Customer management
  const selectedCustomer = ref<Customer | null>(null);
  const selectedAddress = ref<any>(null);

  // Simple ID-only reference to minimize reactivity
  const selectedAddressId = computed(() => selectedAddress.value?.id || null);

  // Log changes to selectedCustomer and selectedAddress
  watch(() => selectedCustomer.value, (newVal, oldVal) => {
    // Customer changed
  }, { deep: true });

  watch(() => selectedAddress.value, (newVal, oldVal) => {
    // Address changed
  }, { deep: true });

  // Validate customer and address selection based on order type
  const isCustomerSelectionValid = computed(() => {
    const orderType = form.value.type;
    
    // For Dine-in, no customer validation needed
    if (orderType === 'Dine-in') {
      return true;
    }
    
    // For Takeaway, need customer selected unless allowTakeawayWithoutCustomer is enabled
    if (orderType === 'Takeaway') {
      return allowTakeawayWithoutCustomer?.value || selectedCustomer.value !== null;
    }
    
    // For Delivery, need customer and address selected
    if (orderType === 'Delivery') {
      return selectedCustomer.value !== null && selectedAddress.value !== null;
    }
    
    return false;
  });

  // Load customer and address data for delivery/takeaway orders (edit mode)
  const loadCustomerDataFromOrder = (order: any) => {
    const backendOrderType = (order as any).order_type || 'dine_in';
    
    if (backendOrderType === 'delivery' || backendOrderType === 'takeaway') {
      // Load customer data if available
      if (order.customer) {
        selectedCustomer.value = {
          id: (order.customer as any).id,
          name: (order.customer as any).name,
          phone: (order.customer as any).phone,
          address: (order.customer as any).address,
          apartment: (order.customer as any).apartment,
          neighborhood: (order.customer as any).neighborhood,
          city: (order.customer as any).city,
          postal_code: (order.customer as any).postal_code,
          reference_point: (order.customer as any).reference_point,
          notes: (order.customer as any).notes,
          addresses: (order.customer as any).addresses || [],
          is_active: (order.customer as any).is_active ?? true,
          order_count: (order.customer as any).order_count ?? 0,
          restaurant_id: (order.customer as any).restaurant_id ?? 0,
          created_at: (order.customer as any).created_at ?? new Date().toISOString(),
          updated_at: (order.customer as any).updated_at ?? new Date().toISOString(),
          full_address: (order.customer as any).full_address || '',
          display_name: (order.customer as any).display_name || (order.customer as any).name,
          has_multiple_addresses: (order.customer as any).has_multiple_addresses ?? false,
          default_address: (order.customer as any).default_address
        };
      }

      // Load selected address for delivery orders
      if (backendOrderType === 'delivery' && (order as any).selected_address) {
        selectedAddress.value = {
          id: (order as any).selected_address.id,
          address: (order as any).selected_address.address,
          apartment: (order as any).selected_address.apartment,
          neighborhood: (order as any).selected_address.neighborhood,
          city: (order as any).selected_address.city,
          postal_code: (order as any).selected_address.postal_code,
          reference_point: (order as any).selected_address.reference_point,
          is_default: (order as any).selected_address.is_default
        };
      } else if (backendOrderType === 'delivery' && (order as any).address_id && (order as any).customer?.addresses) {
        // Fallback: find address by address_id
        const address = (order as any).customer.addresses.find((addr: any) => addr.id === (order as any).address_id);
        if (address) {
          selectedAddress.value = {
            id: address.id,
            address: address.address,
            apartment: address.apartment,
            neighborhood: address.neighborhood,
            city: address.city,
            postal_code: address.postal_code,
            reference_point: address.reference_point,
            is_default: address.is_default
          };
        }
      }
    }
  };

  // Customer selection handler
  const handleCustomerSelected = (customer: Customer) => {
    selectedCustomer.value = customer;
    form.value.customerName = customer.name;
  };

  // Function to handle create customer request
  const handleCreateCustomer = () => {
    // This will be handled by the CustomerSelector component
  };

  // Function to handle address selection
  const handleAddressSelected = (address: any) => {
    // Only update if the address ID actually changed
    if (!selectedAddress.value || selectedAddress.value.id !== address.id) {
      selectedAddress.value = address;
    }
  };

  // Function to clear selected customer
  const clearSelectedCustomer = () => {
    selectedCustomer.value = null;
    selectedAddress.value = null; // Also clear the address when customer is cleared
    form.value.customerName = '';
  };

  // Helper function to get delivery address from form data
  const getDeliveryAddressFromForm = (): string => {
    if (form.value.type !== 'Delivery') return '';
    
    // If we have selectedAddress, use it
    if (selectedAddress.value) {
      const addr = selectedAddress.value;
      const parts: string[] = [];
      if (addr.address) parts.push(addr.address);
      if (addr.apartment) parts.push(`#${addr.apartment}`);
      if (addr.neighborhood) parts.push(`Col. ${addr.neighborhood}`);
      if (addr.city) parts.push(addr.city);
      if (addr.postal_code) parts.push(`CP ${addr.postal_code}`);
      if (addr.reference_point) parts.push(`Ref: ${addr.reference_point}`);
      return parts.join(', ');
    }
    
    // Fallback to customer address
    if (selectedCustomer.value) {
      const cust = selectedCustomer.value;
      const parts: string[] = [];
      if (cust.address) parts.push(cust.address);
      if (cust.apartment) parts.push(`#${cust.apartment}`);
      if (cust.neighborhood) parts.push(`Col. ${cust.neighborhood}`);
      if (cust.city) parts.push(cust.city);
      if (cust.postal_code) parts.push(`CP ${cust.postal_code}`);
      return parts.join(', ');
    }
    
    return '';
  };

  // Watch for customer selection changes
  watch(() => selectedCustomer.value, (newCustomer) => {
    if (newCustomer) {
      form.value.customerName = newCustomer.name;
    } else {
      form.value.customerName = '';
    }
  });

  // Reset customer state
  const resetCustomerState = () => {
    selectedCustomer.value = null;
    selectedAddress.value = null;
  };

  return {
    // State
    selectedCustomer,
    selectedAddress,
    selectedAddressId,
    isCustomerSelectionValid,
    
    // Actions
    loadCustomerDataFromOrder,
    handleCustomerSelected,
    handleCreateCustomer,
    handleAddressSelected,
    clearSelectedCustomer,
    resetCustomerState,
    
    // Helpers
    getDeliveryAddressFromForm
  };
}
